import { type DialogProps } from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { Search, X } from 'lucide-react';
import * as React from 'react';

import { CONTROL_SIZE } from './types/control-enums';

import { DIALOG_DISMISSAL, DIALOG_SIZE } from './types/dialog-variants-enums';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { COMMAND_APPEARANCE } from './types/command-variants-enums';



type CommandProps = React.ComponentPropsWithoutRef<typeof CommandPrimitive> & {
  appearance?: COMMAND_APPEARANCE;
};

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  CommandProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className
      )}
      {...props}
    />
  );
});
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent
        className="overflow-hidden p-0 shadow-card-elevated"
        dismissal={DIALOG_DISMISSAL.STANDARD}
        size={DIALOG_SIZE.MD}
      >
        <Command
          appearance={COMMAND_APPEARANCE.DEFAULT}
          className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-content-secondary [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const commandInputFoundationVariants = cva(
  'flex w-full rounded-md bg-transparent text-content-primary outline-none placeholder:text-content-tertiary disabled:cursor-not-allowed disabled:text-content-disabled disabled:opacity-100 pointer-coarse:h-11 pointer-coarse:text-base',
  {
    variants: {
      size: {
        [CONTROL_SIZE.COMPACT]: 'h-9 py-2 type-field-label',
        [CONTROL_SIZE.DEFAULT]: 'h-10 py-2.5 type-body',
      },
    },
  }
);

interface CommandInputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>, 'size'> {
  className?: string;
  onClear?: () => void;
  size?: CONTROL_SIZE.COMPACT | CONTROL_SIZE.DEFAULT;
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  CommandInputProps
>(({ className, value, onClear, size, ...props }, ref) => {
  const resolvedSize = size ?? CONTROL_SIZE.DEFAULT;
  const inputClassName = commandInputFoundationVariants({ size: resolvedSize });

  return (
    <div
      className="flex items-center border-b px-3"
      data-size={resolvedSize}
      style={{ pointerEvents: 'auto' }}
    >
      <Search className="mr-2 size-4 shrink-0 text-content-tertiary" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(inputClassName, className)}
        style={{ pointerEvents: 'auto' }}
        value={value}
        {...props}
      />
      {value && onClear ? (
        <button
          aria-label="Clear search"
          className="ml-2 rounded-control text-content-tertiary transition-colors duration-standard hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          onClick={onClear}
          type="button"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
});

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, style, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden overscroll-contain', className)}
    style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', ...style }}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty ref={ref} className="py-6 text-center type-body" {...props} />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:type-settings-row-meta **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-content-secondary',
      className
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 h-px bg-border', className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const foundationCommandItemClassName =
  'relative flex cursor-default select-none items-center rounded-affordance px-2 py-1.5 type-body text-content-primary outline-none transition-[color,background-color] duration-standard ease-standard motion-reduce:transition-none aria-selected:bg-neutral-interaction-hover aria-selected:text-content-primary data-[state=checked]:bg-neutral-interaction-selected data-[state=checked]:text-content-primary pointer-coarse:min-h-11';

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(foundationCommandItemClassName, className)}
      {...props}
    />
  );
});

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('ml-auto type-settings-row-meta tracking-widest text-content-tertiary', className)}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut };
